import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import { VehicleCheckInDetailsDto, vehicleCheckInDetailsValidationSchema } from 'dtos'
import { Formik } from 'formik'

interface VehicleCheckInEditDialogProps {
  disabled?: boolean
  initialValues: VehicleCheckInDetailsDto
  open: boolean
  isLoading: boolean
  onClose: () => void
  onSave: (values: any) => void
}

export default function VehicleCheckInEditDialog({
  disabled,
  onClose,
  open,
  initialValues,
  isLoading,
  onSave
}: VehicleCheckInEditDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={vehicleCheckInDetailsValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setValues, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle>Vehicle Details</DialogTitle>

              <DialogContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(
                                touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage
                              )}
                              helperText={
                                touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage
                              }
                              label='Odometer Reading'
                              name='vehicleCheckInOdometerReadingMileage'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInOdometerReadingMileage}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInOdometerReadingUnitOfMeasurement'
                              row
                              value={values.vehicleCheckInOdometerReadingUnitOfMeasurement}
                              onChange={event => {
                                const { value } = event.target
                                handleChange(event)
                                setFieldValue('convertCluster', value === 'Kilometers')
                              }}
                            >
                              <FormControlLabel
                                value='Miles'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Miles'
                              />
                              <FormControlLabel
                                value='Kilometers'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Kilometers'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant='body1' color='textSecondary' style={{ display: 'flex', alignItems: 'center' }}>
                              Convert Cluster
                            </Typography>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <RadioGroup name='convertCluster' onChange={handleChange} row value={values.convertCluster}>
                              <FormControlLabel
                                value='true'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Yes'
                              />
                              <FormControlLabel
                                value='false'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='No'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant='body1' color='textSecondary' style={{ display: 'flex', alignItems: 'center' }}>
                              Program Cluster
                            </Typography>
                          </Grid>

                          <Grid item xs={6} sm={6}>
                            <RadioGroup
                              name='programCluster'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInAdditionalProgramming}
                            >
                              <FormControlLabel
                                value='true'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Yes'
                              />
                              <FormControlLabel
                                value='false'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='No'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant='body1' color='textSecondary'>
                              Manufacturered Date
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInMFGBuildDateMonth && errors.vehicleCheckInMFGBuildDateMonth)}
                              helperText={touched.vehicleCheckInMFGBuildDateMonth && errors.vehicleCheckInMFGBuildDateMonth}
                              label='MFG Build Date (Month)'
                              name='vehicleCheckInMFGBuildDateMonth'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInMFGBuildDateMonth || ''}
                            >
                              <MenuItem value={1}>1</MenuItem>
                              <MenuItem value={2}>2</MenuItem>
                              <MenuItem value={3}>3</MenuItem>
                              <MenuItem value={4}>4</MenuItem>
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={6}>6</MenuItem>
                              <MenuItem value={7}>7</MenuItem>
                              <MenuItem value={8}>8</MenuItem>
                              <MenuItem value={9}>9</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={11}>11</MenuItem>
                              <MenuItem value={12}>12</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInMFGBuildDateYear && errors.vehicleCheckInMFGBuildDateYear)}
                              helperText={touched.vehicleCheckInMFGBuildDateYear && errors.vehicleCheckInMFGBuildDateYear}
                              label='MFG Build Date (Year)'
                              name='vehicleCheckInMFGBuildDateYear'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInMFGBuildDateYear || ''}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <Typography fontWeight='bold' variant='body1'>
                              Weight
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInWeightGVWR && errors.vehicleCheckInWeightGVWR)}
                              helperText={touched.vehicleCheckInWeightGVWR && errors.vehicleCheckInWeightGVWR}
                              label='GVWR'
                              name='vehicleCheckInWeightGVWR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInWeightGVWR}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInWeightGVWRUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInWeightGVWRUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='KG'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Kg'
                              />
                              <FormControlLabel
                                value='LB'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Lb'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GVWR (Kg)' value={values.vehicleCheckInWeightGVWRKg?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GVWR (Lbs)' value={values.vehicleCheckInWeightGVWRLbs?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12}>
                            <LabelField label='Curb Weight' value={values.vehicleCheckInCurbWeight?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInWeightGAWRFR && errors.vehicleCheckInWeightGAWRFR)}
                              helperText={touched.vehicleCheckInWeightGAWRFR && errors.vehicleCheckInWeightGAWRFR}
                              label='GAWR FR'
                              name='vehicleCheckInWeightGAWRFR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInWeightGAWRFR}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInWeightGAWRFRUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInWeightGAWRFRUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='KG'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Kg'
                              />
                              <FormControlLabel
                                value='LB'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Lb'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GAWR FR (Kg)' value={values.vehicleCheckInWeightGAWRFRKg?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GAWR FR (Lbs)' value={values.vehicleCheckInWeightGAWRFRLbs?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInWeightGAWRRR && errors.vehicleCheckInWeightGAWRRR)}
                              helperText={touched.vehicleCheckInWeightGAWRRR && errors.vehicleCheckInWeightGAWRRR}
                              label='GAWR RR'
                              name='vehicleCheckInWeightGAWRRR'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInWeightGAWRRR}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInWeightGAWRRRUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInWeightGAWRRRUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='KG'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Kg'
                              />
                              <FormControlLabel
                                value='LB'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='Lb'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GAWR RR (Kg)' value={values.vehicleCheckInWeightGAWRRRKg?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='GAWR RR (Lbs)' value={values.vehicleCheckInWeightGAWRRRLbs?.toFixed(2)} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={12}>
                            <Typography fontWeight='bold' variant='body1'>
                              Tires
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInFrontTires && errors.vehicleCheckInFrontTires)}
                              helperText={touched.vehicleCheckInFrontTires && errors.vehicleCheckInFrontTires}
                              label='Front Tires'
                              name='vehicleCheckInFrontTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.vehicleCheckInFrontTires}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInFrontRims && errors.vehicleCheckInFrontRims)}
                              helperText={touched.vehicleCheckInFrontRims && errors.vehicleCheckInFrontRims}
                              label='Front Rims'
                              name='vehicleCheckInFrontRims'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.vehicleCheckInFrontRims}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(
                                touched.vehicleCheckInFrontTiresPressure && errors.vehicleCheckInFrontTiresPressure
                              )}
                              helperText={touched.vehicleCheckInFrontTiresPressure && errors.vehicleCheckInFrontTiresPressure}
                              label='Front Tires PSI'
                              name='vehicleCheckInFrontTiresPressure'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInFrontTiresPressure}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInFrontTiresPressureUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInFrontTiresPressureUnitOfMeasurement}
                            >
                              <FormControlLabel
                                value='kPa'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='kPa'
                              />
                              <FormControlLabel
                                value='PSI'
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='PSI'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Front Tires (kPa)' value={values.vehicleCheckInFrontTiresPressurekPa?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Front Tires (PSI)' value={values.vehicleCheckInFrontTiresPressurePSI?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInRearTires && errors.vehicleCheckInRearTires)}
                              helperText={touched.vehicleCheckInRearTires && errors.vehicleCheckInRearTires}
                              label='Rear Tires'
                              name='vehicleCheckInRearTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.vehicleCheckInRearTires}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInRearRims && errors.vehicleCheckInRearRims)}
                              helperText={touched.vehicleCheckInRearRims && errors.vehicleCheckInRearRims}
                              label='Rear Rims'
                              name='vehicleCheckInRearRims'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='string'
                              value={values.vehicleCheckInRearRims}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInRearTiresPressure && errors.vehicleCheckInRearTiresPressure)}
                              helperText={touched.vehicleCheckInRearTiresPressure && errors.vehicleCheckInRearTiresPressure}
                              label='Rear Tires PSI'
                              name='vehicleCheckInRearTiresPressure'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              value={values.vehicleCheckInRearTiresPressure}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <RadioGroup
                              name='vehicleCheckInRearTiresPressureUnitOfMeasurement'
                              onChange={handleChange}
                              row
                              value={values.vehicleCheckInRearTiresPressureUnitOfMeasurement}
                            >
                              <FormControlLabel
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='kPa'
                                value='kPa'
                              />
                              <FormControlLabel
                                control={<Radio disabled={isLoading || isSubmitting || disabled} />}
                                label='PSI'
                                value='PSI'
                              />
                            </RadioGroup>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Rear Tires (kPa)' value={values.vehicleCheckInRearTiresPressurekPa?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <LabelField label='Rear Tires (PSI)' value={values.vehicleCheckInRearTiresPressurePSI?.toFixed(2)} />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              disabled={isLoading || isSubmitting || disabled}
                              error={Boolean(touched.vehicleCheckInNumberOfRearTires && errors.vehicleCheckInNumberOfRearTires)}
                              helperText={touched.vehicleCheckInNumberOfRearTires && errors.vehicleCheckInNumberOfRearTires}
                              label='Number Of Rear Tires'
                              name='vehicleCheckInNumberOfRearTires'
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type='number'
                              select
                              value={values.vehicleCheckInNumberOfRearTires}
                            >
                              <MenuItem value={2}>2</MenuItem>
                              <MenuItem value={4}>4</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12}>
                            <LabelField label='Rear Tire Config' value={values.vehicleCheckInRearTireConfig} />
                          </Grid>

                          <Grid item xs={12}>
                            <ExplanationAccordion variant='outlined'>
                              The information entered by users during vehicle check-in will be displayed here. All fields are
                              required.
                            </ExplanationAccordion>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton disabled={disabled} loading={isSubmitting || isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
